import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsAuthenticated } from '../../auth/authSlice';
import {
  selectEffectivePlatformPermissions,
  selectPlatformPermissionsStatus,
} from '../platformPermissionsSlice';
import { hasPlatformEdit, hasPlatformVisibility } from '../platformAccessLevel';
import {
  selectEnabledStandards,
  selectOrganizationStandardsStatus,
  selectPermissionStandardsMap,
} from '../../organizationStandards/organizationStandardsSlice';
import { isModuleVisibleForStandards } from '../../../constants/isoStandards';

interface RequirePermissionProps {
  permission: string;
  minLevel?: 'view' | 'edit';
}

/**
 * Protege rutas según permisos efectivos del usuario (BD + overrides).
 * Debe ir dentro de RequireAuth.
 */
export const RequirePermission = ({ permission, minLevel = 'view' }: RequirePermissionProps) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const effective = useSelector(selectEffectivePlatformPermissions);
  const status = useSelector(selectPlatformPermissionsStatus);
  const enabledStandards = useSelector(selectEnabledStandards);
  const permissionStandards = useSelector(selectPermissionStandardsMap);
  const standardsStatus = useSelector(selectOrganizationStandardsStatus);
  const location = useLocation();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    status === 'loading' ||
    effective === null ||
    standardsStatus === 'loading' ||
    standardsStatus === 'idle' ||
    enabledStandards === null ||
    permissionStandards === null
  ) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
        Cargando permisos…
      </div>
    );
  }

  if (status === 'failed' || standardsStatus === 'failed') {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 px-4 text-center text-sm text-destructive">
        <p>No se pudieron cargar los permisos.</p>
        <p className="text-muted-foreground">Cierra sesión y vuelve a entrar.</p>
      </div>
    );
  }

  const allowed =
    minLevel === 'edit'
      ? hasPlatformEdit(effective[permission])
      : hasPlatformVisibility(effective[permission]);

  const standardsAllowed = isModuleVisibleForStandards(
    permission,
    enabledStandards,
    permissionStandards,
  );

  if (!allowed || !standardsAllowed) {
    return <Navigate to="/perfil" replace />;
  }

  return <Outlet />;
};
